import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, normalizeClass as _normalizeClass, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "container-fluid" }
const _hoisted_2 = { class: "row starter-main mt-5 pt-5" }
const _hoisted_3 = { class: "d-flex justify-content-end align-items-center gap-3 mt-3 mb-4" }
const _hoisted_4 = { class: "d-flex align-items-center gap-2" }
const _hoisted_5 = ["value"]
const _hoisted_6 = { class: "pagination-mini" }
const _hoisted_7 = ["disabled"]
const _hoisted_8 = ["disabled", "onClick"]
const _hoisted_9 = ["disabled"]

import { ref, computed, onMounted, watch } from "vue";
import { useCrud } from "@/composables/commen/useCrud";
import BaseModal from "@/components/commen/modal/BaseModal.vue";
import BaseForm, { FormField } from "@/components/commen/modal/BaseForm.vue";
import TableTitle from "@/components/commen/table/TableTitle.vue";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";

const API_RESOURCE = "/common/sitemaps";
const pageTitle = "Sitemap";
const title = "Sitemaps List";

// Use the generic CRUD composable

export default /*@__PURE__*/_defineComponent({
  __name: 'SiteMap',
  setup(__props) {

const crud = useCrud(API_RESOURCE);

// pagination local UI state
const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const currentPage = ref(1);

// visiblePages computed
const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  if (!total || total <= 4) {
    return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  pages.push(1);

  if (current > 4) pages.push("...");

  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);

  if (current < total - 3) pages.push("...");

  pages.push(total);
  return pages;
});

// Table configuration
const rows = ref<any[]>([]);
const tableConfig = ref({
  columns: [
    { name: "URL", field: "url", sortable: true, width: "300px" },
    { name: "Priority", field: "priority", sortable: true, width: "100px" },
    { name: "Change Freq", field: "changefreq", sortable: true, width: "150px" },
    { name: "Last Modification", field: "lastmod", sortable: true, width: "200px" },
    { name: "Active", field: "is_active", type:'toggle_status', sortable: false, width: "80px" },
  ],
  data: rows,
  actions: [true],
  loading: crud.isLoading,
  showStatusFilter: false,
  table_width: "max-content",
});

watch(crud.data, (newVal) => {
  rows.value = newVal || [];
});
watch(crud.isLoading, (newVal) => {
  tableConfig.value.loading = newVal;
});

// Sync backend pagination into tablePagination
watch(
  () => crud.pagination.value,
  (newVal) => {
    if (!newVal) return;

    tablePagination.value.current_page = newVal.current_page || 1;
    tablePagination.value.total_pages = newVal.total_pages || 1;
    tablePagination.value.total = newVal.total || 0;
    tablePagination.value.per_page = tablePagination.value.per_page;
    tablePagination.value.has_prev = tablePagination.value.current_page > 1;
    tablePagination.value.has_next =
    tablePagination.value.current_page < tablePagination.value.total_pages;
  },
  { immediate: true, deep: true }
);

// === ACTIONS ===
const changeRows = async () => {
  currentPage.value = 1;
  await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
};

const handlePageChange = async (page: number) => {
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (
    tablePagination.value.total_pages &&
    page > tablePagination.value.total_pages
  )
    return;

  currentPage.value = page;
  await crud.fetchData({ page, limit: tablePagination.value.per_page });
};

// --- Modal State ---
const isCreateModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isViewMode = ref(false);

// Formatting date for HTML5 input
const formatDate = (date: any) => {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
};

// --- Form Data ---
const newItemData = ref({
  url: "",
  priority: 0.5,
  changefreq: "weekly",
  lastmod: formatDate(new Date()),
  is_active: true,
});

const editingItemData = ref<any>({
  _id: "",
  url: "",
  priority: 0.5,
  changefreq: "weekly",
  lastmod: "",
  is_active: true,
});

const changefreqOptions = [
  { _id: "always", title: "Always" },
  { _id: "hourly", title: "Hourly" },
  { _id: "daily", title: "Daily" },
  { _id: "weekly", title: "Weekly" },
  { _id: "monthly", title: "Monthly" },
  { _id: "yearly", title: "Yearly" },
  { _id: "never", title: "Never" },
];

// --- Form Definitions ---
const createFormFields: FormField[] = [
  {
    key: "url",
    label: "URL",
    type: "text",
    required: true,
    placeholder: "Enter URL",
  },
  {
    key: "priority",
    label: "Priority",
    type: "number",
    required: false,
    placeholder: "Enter priority (0.0 to 1.0)",
  },
  {
    key: "changefreq",
    label: "Change Frequency",
    type: "select",
    required: false,
    options: changefreqOptions,
  },
  {
    key: "lastmod",
    label: "Last Modified",
    type: "date",
    required: false,
  },
  {
    key: "is_active",
    label: "Active",
    type: "checkbox",
    required: false,
  },
];

const editFormFields = computed<FormField[]>(() => [
  {
    key: "url",
    label: "URL",
    type: "text",
    required: true,
    placeholder: "Enter URL",
    readonly: isViewMode.value,
  },
  {
    key: "priority",
    label: "Priority",
    type: "number",
    required: false,
    placeholder: "Enter priority (0.0 to 1.0)",
    readonly: isViewMode.value,
  },
  {
    key: "changefreq",
    label: "Change Frequency",
    type: "select",
    required: false,
    options: changefreqOptions,
    readonly: isViewMode.value,
  },
  {
    key: "lastmod",
    label: "Last Modified",
    type: "date",
    required: false,
    readonly: isViewMode.value,
  },
  {
    key: "is_active",
    label: "Active",
    type: "checkbox",
    required: false,
    readonly: isViewMode.value,
  },
]);

// --- Handlers ---
const handleEdit = (row: any) => {
  isViewMode.value = false;
  editingItemData.value = { 
    ...row,
    lastmod: formatDate(row.lastmod)
  };
  isEditModalOpen.value = true;
};

const handleView = (row: any) => {
  isViewMode.value = true;
  editingItemData.value = { 
    ...row,
    lastmod: formatDate(row.lastmod)
  };
  isEditModalOpen.value = true;
};

const handleDelete = async (id: string) => {
  if (confirm("Are you sure you want to delete this sitemap?")) {
    await crud.deleteItem(id);
    await crud.fetchData({
      page: tablePagination.value.current_page,
      limit: tablePagination.value.per_page,
    });
  }
};

const handleSaveCreate = async () => {
  if (!newItemData.value.url?.toString().trim()) {
    alert("URL is required.");
    return;
  }

  const result = await crud.createItem(newItemData.value);
  if (result) {
    closeCreateModal();
    await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
  }
};

const handleSaveEdit = async () => {
  const id = editingItemData.value._id;
  if (!id) return;
  const { _id, ...updatePayload } = editingItemData.value;
  const result = await crud.updateItem(id, updatePayload);
  if (result) {
    closeEditModal();
    await crud.fetchData({
      page: tablePagination.value.current_page,
      limit: tablePagination.value.per_page,
    });
  }
};

const toggleStatus = async (id: string, status: boolean) => {
  if (!id) return;

  try {
    const result = await crud.updateItem(id, {
      is_active: status
    });

    if (result) {
      const row = rows.value.find(r => r._id === id);
      if (row) {
        row.is_active = status;
      }
      await crud.fetchData({
        page: tablePagination.value.current_page,
        limit: tablePagination.value.per_page,
      });
    }

  } catch (err) {
    console.error("Failed to update status:", err);
  }
};

const closeCreateModal = () => {
  isCreateModalOpen.value = false;
  newItemData.value = {
    url: "",
    priority: 0.5,
    changefreq: "weekly",
    lastmod: formatDate(new Date()),
    is_active: true,
  };
};

const closeEditModal = () => {
  isEditModalOpen.value = false;
  isViewMode.value = false;
  editingItemData.value = {
    _id: "",
    url: "",
    priority: 0.5,
    changefreq: "weekly",
    lastmod: "",
    is_active: true,
  };
};

// --- lifecycle: initial load ---
onMounted(async () => {
  await crud.fetchData({
    page: tablePagination.value.current_page,
    limit: tablePagination.value.per_page,
  });
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(TableTitle, {
          title: title,
          pageTitle: pageTitle,
          icon: "fas fa-sitemap",
          showAction: true,
          actionLabel: "Create New",
          modal: 'createNew'
        }),
        _createVNode(CommonTable2, {
          tableObj: tableConfig.value,
          onEdit: handleEdit,
          onDelete: handleDelete,
          onView: handleView,
          onToggleStatus: toggleStatus
        }, null, 8, ["tableObj"])
      ])
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _cache[5] || (_cache[5] = _createElementVNode("span", { class: "text-muted" }, "Rows per page:", -1)),
        _withDirectives(_createElementVNode("select", {
          class: "form-select form-select-sm rows-select",
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((tablePagination.value.per_page) = $event)),
          onChange: changeRows
        }, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList([5, 10, 20, 50, 100], (n) => {
            return _createElementVNode("option", {
              key: n,
              value: n
            }, _toDisplayString(n), 9, _hoisted_5)
          }), 64))
        ], 544), [
          [_vModelSelect, tablePagination.value.per_page]
        ])
      ]),
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("button", {
          class: "page-btn prev_btn",
          disabled: !tablePagination.value.has_prev,
          onClick: _cache[1] || (_cache[1] = ($event: any) => (handlePageChange(tablePagination.value.current_page - 1)))
        }, " « ", 8, _hoisted_7),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visiblePages.value, (p, index) => {
          return (_openBlock(), _createElementBlock("button", {
            key: index,
            class: _normalizeClass(["page-btn", {
          'page-active': p === tablePagination.value.current_page,
          'disabled-dots': p === '...',
        }]),
            disabled: p === '...',
            onClick: ($event: any) => (p !== '...' && typeof p === 'number' && handlePageChange(p) )
          }, _toDisplayString(p), 11, _hoisted_8))
        }), 128)),
        _createElementVNode("button", {
          class: "page-btn next_btn",
          disabled: !tablePagination.value.has_next,
          onClick: _cache[2] || (_cache[2] = ($event: any) => (handlePageChange(tablePagination.value.current_page + 1)))
        }, " » ", 8, _hoisted_9)
      ])
    ]),
    _createVNode(BaseModal, {
      id: "createNew",
      title: `Create ${pageTitle}`,
      visible: isCreateModalOpen.value,
      onClose: closeCreateModal,
      onSave: handleSaveCreate,
      saveButtonText: "Create"
    }, {
      body: _withCtx(() => [
        _createVNode(BaseForm, {
          fields: createFormFields,
          modelValue: newItemData.value,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((newItemData).value = $event)),
          onSubmit: handleSaveCreate,
          ref: "createFormRef"
        }, null, 8, ["modelValue"])
      ]),
      _: 1
    }, 8, ["title", "visible"]),
    _createVNode(BaseModal, {
      id: "editModal",
      title: `${isViewMode.value ? 'View' : 'Edit'} ${pageTitle}`,
      visible: isEditModalOpen.value,
      onClose: closeEditModal,
      onSave: handleSaveEdit,
      "show-save-button": !isViewMode.value
    }, {
      body: _withCtx(() => [
        _createVNode(BaseForm, {
          fields: editFormFields.value,
          modelValue: editingItemData.value,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((editingItemData).value = $event)),
          onSubmit: handleSaveEdit,
          ref: "editFormRef"
        }, null, 8, ["fields", "modelValue"])
      ]),
      _: 1
    }, 8, ["title", "visible", "show-save-button"])
  ], 64))
}
}

})